// Version comparison (spec §5, ADR-0003): pure, no I/O. Input: the rows of the previous and the new version of one source type.
import type { IntegrityResponse } from "./api";
import type { Fields } from "./rows";

export type VersionRow = { key: string[]; rowHash: string; fields: Fields };
export type FieldChange = { field: string; old: string | number | null; new: string | number | null };
export type VersionDiff = {
  newCount: number;
  /** Same key, different row fingerprint (DATA_CHANGED). Order of the new version. */
  changed: { key: string[]; fields: FieldChange[] }[];
  /** Key in the previous version, absent from the new one (DATA_REMOVED). Order of the previous version. */
  removed: { key: string[]; fields: Fields }[];
};

/** `recordKey` of the API (glossary §7): parts joined with "|"; inside a part "\" is written "\\" and "|" is written "\|", so ["a|b","c"] differs from ["a","b|c"]. */
export const recordKeyOf = (key: string[]) => key.map((p) => p.replace(/[\\|]/g, "\\$&")).join("|");

/** Inverse of recordKeyOf. */
export function parseRecordKey(s: string): string[] {
  const parts = [""];
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (ch === "\\" && i + 1 < s.length) parts[parts.length - 1] += s[++i];
    else if (ch === "|") parts.push("");
    else parts[parts.length - 1] += ch;
  }
  return parts;
}

/** Field by field, old -> new. A field present on one side only counts as null on the other. */
export function fieldChanges(old: Fields, now: Fields): FieldChange[] {
  const names = [...new Set([...Object.keys(old), ...Object.keys(now)])].sort();
  return names.flatMap((field) => {
    const o = old[field] ?? null, n = now[field] ?? null;
    return o === n ? [] : [{ field, old: o, new: n }];
  });
}

export function diffVersions(prev: VersionRow[], next: VersionRow[]): VersionDiff {
  const before = new Map(prev.map((r) => [recordKeyOf(r.key), r]));
  const after = new Set(next.map((r) => recordKeyOf(r.key)));
  const out: VersionDiff = { newCount: 0, changed: [], removed: [] };
  for (const r of next) {
    const old = before.get(recordKeyOf(r.key));
    if (!old) out.newCount++;
    else if (old.rowHash !== r.rowHash) out.changed.push({ key: r.key, fields: fieldChanges(old.fields, r.fields) });
  }
  for (const r of prev) if (!after.has(recordKeyOf(r.key))) out.removed.push({ key: r.key, fields: r.fields });
  return out;
}

export type Period = { start: string; end: string };

/**
 * ADR-0018 period upload: new version = carried ∪ file. Carried = previous rows whose event date (`fields[dateField]`, "YYYY-MM-DD", text compare)
 * lies outside the period (both ends inclusive) and whose key is not in the file. The file wins on identity, so a record that moved out of the
 * period is not carried and diffs as DATA_CHANGED. A previous row without a date is outside every period (kept).
 * Rows pass through (row_no, manual_entry_id ride along in T). Order: carried first, then file.
 * Source contract (#318): a carried row keeps the `sourceUploadId` it is given. The caller passes the effective source (api/records.ts sourceUploadIdSql: own value, else the
 * previous upload when it is a file, else null for a manual-entry version); the reader fallback (same expression)
 * is only for old rows no writer ever touched.
 */
export function carryOver<T extends VersionRow & { sourceUploadId?: number | null }>(prev: T[], fileRows: T[], period: Period, dateField: string): { rows: T[]; carried: T[] } {
  const inFile = new Set(fileRows.map((r) => recordKeyOf(r.key)));
  const carried = prev.filter((r) => {
    const d = r.fields[dateField];
    const inside = typeof d === "string" && d.slice(0, 10) >= period.start && d.slice(0, 10) <= period.end;
    return !inside && !inFile.has(recordKeyOf(r.key));
  });
  return { rows: [...carried, ...fileRows], carried };
}

/** A real calendar date written YYYY-MM-DD. */
export const isIsoDate = (s: unknown): s is string => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(s)) && new Date(s).toISOString().startsWith(s);

/** ADR-0018 user decision 1: the whole months the event dates cover (first day of the earliest month .. last day of the latest). Null when no dates. */
export function suggestPeriod(dates: (string | null | undefined)[]): Period | null {
  const ds = dates.filter((d): d is string => typeof d === "string" && isIsoDate(d.slice(0, 10))).map((d) => d.slice(0, 10)).sort();
  if (!ds.length) return null;
  const [lo, hi] = [ds[0]!, ds[ds.length - 1]!];
  const last = new Date(Date.UTC(+hi.slice(0, 4), +hi.slice(5, 7), 0)).getUTCDate();
  return { start: `${lo.slice(0, 7)}-01`, end: `${hi.slice(0, 7)}-${String(last).padStart(2, "0")}` };
}

/** `DATA_CHANGED|ERP_STOK|GIR-4598|14`: the last part is the uploading version, so the same record can change in two uploads in a row (ADR-0003). */
export const dataFindingKey = (rule: "DATA_CHANGED" | "DATA_REMOVED", sourceType: string, key: string[], uploadId: number) =>
  [rule, sourceType, recordKeyOf(key), uploadId].join("|");

/** 117000 -> "117.000", 12.5 -> "12,5" (Turkish, up to 4 decimals). */
export const formatNumber = (n: number) =>
  n.toLocaleString("en-US", { maximumFractionDigits: 4 }).replace(/[,.]/g, (c) => (c === "," ? "." : ","));

/** Display precision (PM decision): kg whole, USD 2 decimals. Values stay exact in the API. */
export const formatKg = (n: number) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(n);
export const formatUsd = (n: number) => new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

/** "2026-10-01" -> "01.10.2026" (reorder only; API dates are ISO). */
export const formatDate = (iso: string) => iso.slice(0, 10).split("-").reverse().join(".");

/** Days to a DİİB end date; negative means it has passed. */
export const daysLeftText = (d: number) => (d < 0 ? `${formatNumber(-d)} gün önce sona erdi` : `${formatNumber(d)} gün`);

/** 99.4 -> "%99,4" (always one decimal). */
export const formatPct = (n: number) => `%${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n)}`;

/** E6 difference line: "117.000 → 120.000 (+3.000)". Non-numbers: "A → B". */
export function formatChange(old: string | number | null, now: string | number | null): string {
  const show = (v: string | number | null) => (v === null ? "—" : typeof v === "number" ? formatNumber(v) : v);
  const base = `${show(old)} → ${show(now)}`;
  if (typeof old !== "number" || typeof now !== "number") return base;
  const d = Math.round((now - old) * 1e4) / 1e4;
  return `${base} (${d > 0 ? "+" : d < 0 ? "−" : ""}${formatNumber(Math.abs(d))})`;
}

/** Spec §5 integrity line. Recorded data changes do not break integrity; they are written next to it. */
export function integrityLine(r: IntegrityResponse): string {
  if (r.failures.length) {
    const manual = r.failures.filter((f) => f.origin === "manual").length;
    const file = r.failures.length - manual;
    return `✗ ${[file && `${file} dosya yüklemeden sonra değişti`, manual && `${manual} elle giriş kaydedildikten sonra değişti`].filter(Boolean).join(" · ")}`;
  }
  const base = r.manualEntryCount
    ? `✓ ${r.fileCount} dosya ve ${r.manualEntryCount} elle giriş, ${formatNumber(r.recordCount)} kayıt — kaydedildikten sonra değişen yok`
    : `✓ ${r.fileCount} dosya, ${formatNumber(r.recordCount)} kayıt — yüklemeden sonra değişen yok`;
  return r.recordedChangeCount ? `${base} · ${r.recordedChangeCount} değişiklik kayıtlı` : base;
}
